import React, { useState, useEffect } from 'react'
import {
  Wrap,
  Container,
  Pop,
  PopUp,
  PopUpContent,
  Coc,
  DaysCount,
  RequestButton,
  StatusBadge,
  PageHeader,
  Title,
  DateCell,
  TypeBadge,
  EmptyState,
} from './historyElements'
import dayjs from 'dayjs'
import 'dayjs/locale/pl' // Dodajemy import lokalizacji polskiej
import VacationRequestForm from './components/VacationRequest'
import { useGetUserVacationRequestsQuery } from '../../../../features/vacations/vacationsApiSlice'
import useOwnUser from 'hooks/useOwnUser'
import * as Response from '../../../../pkg/responses'
import { useTranslation } from 'react-i18next'
import Tables from '../../../root/components/Table'

const CalendarIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='16'
    height='16'
    stroke='currentColor'
    strokeWidth='2'
    fill='none'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect
      x='3'
      y='4'
      width='18'
      height='18'
      rx='2'
      ry='2'
    ></rect>
    <line
      x1='16'
      y1='2'
      x2='16'
      y2='6'
    ></line>
    <line
      x1='8'
      y1='2'
      x2='8'
      y2='6'
    ></line>
    <line
      x1='3'
      y1='10'
      x2='21'
      y2='10'
    ></line>
  </svg>
)
const PlusIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='18'
    height='18'
    stroke='currentColor'
    strokeWidth='2'
    fill='none'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <line
      x1='12'
      y1='5'
      x2='12'
      y2='19'
    ></line>
    <line
      x1='5'
      y1='12'
      x2='19'
      y2='12'
    ></line>
  </svg>
)
const XIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='18'
    height='18'
    stroke='currentColor'
    strokeWidth='2'
    fill='none'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <line
      x1='18'
      y1='6'
      x2='6'
      y2='18'
    ></line>
    <line
      x1='6'
      y1='6'
      x2='18'
      y2='18'
    ></line>
  </svg>
)
const ClockIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='16'
    height='16'
    stroke='currentColor'
    strokeWidth='2'
    fill='none'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle
      cx='12'
      cy='12'
      r='10'
    ></circle>
    <polyline points='12 6 12 12 16 14'></polyline>
  </svg>
)

interface SelectedType {
  name: string
  available_vacation_days?: number
}

interface ComponentProps {
  isFormVisible: boolean
  closeForm: () => void
  refetch: () => void
}

const VacationFormModal: React.FC<ComponentProps> = ({ isFormVisible, closeForm, refetch }) => {
  const { t } = useTranslation()

  if (!isFormVisible) return null

  return (
    <Pop>
      <PopUp>
        <PopUpContent>
          <button
            onClick={closeForm}
            aria-label='Close'
          >
            <XIcon />
          </button>
          <VacationRequestForm
            handleClose={closeForm}
            refetch={refetch}
          />
        </PopUpContent>
      </PopUp>
    </Pop>
  )
}

const formatDate = (date: string): string => {
  return dayjs(date).locale('pl').format('DD MMM YYYY')
}

const HistoryVacation: React.FC = () => {
  const { t } = useTranslation()
  const columns = [
    { header: t('CompanyHistory:History.type'), accessor: 'type' },
    { header: t('CompanyHistory:History.startingDate'), accessor: 'starting_date' },
    { header: t('CompanyHistory:History.finishingDate'), accessor: 'finishing_date' },
    { header: t('CompanyHistory:History.hours'), accessor: 'hours' },
    { header: t('CompanyHistory:History.createdAt'), accessor: 'created_at' },
    { header: t('CompanyHistory:History.status'), accessor: 'status' },
  ]

  const user = useOwnUser()
  const [isFormVisible, setIsFormVisible] = useState(false)
  const {
    data: requests,
    isSuccess,
    isLoading,
    refetch,
  } = useGetUserVacationRequestsQuery(
    { userID: Number(user.userUID), companyID: user.selectedCompanyID },
    { skip: !user },
  )

  const handleOpenForm = () => {
    setIsFormVisible(true)
  }

  const handleCloseForm = () => {
    setIsFormVisible(false)
  }

  const { companyProfiles, setSelectedCompanyID, setGroups, selectedCompanyID } = useOwnUser()
  const profile = companyProfiles?.find((profile) => profile.id === selectedCompanyID)
  const [selected, setSelected] = useState<Response.CompanyProfile | undefined>(profile)

  const handleProfileChange = (profile: Response.CompanyProfile): void => {
    setSelected(profile)
    setSelectedCompanyID(profile.id)
    setGroups(profile.groups)
  }

  useEffect(() => {
    if (companyProfiles && !selected) {
      handleProfileChange(companyProfiles[0])
    }
  }, [companyProfiles, selected])

  if (!selected) return <></>

  const cellRenderer = (dataKey: string, row: any): JSX.Element => {
    if (dataKey === 'starting_date' || dataKey === 'finishing_date') {
      return (
        <DateCell>
          <CalendarIcon />
          <span>{formatDate(row[dataKey])}</span>
        </DateCell>
      )
    }

    if (dataKey === 'created_at') {
      return (
        <DateCell>
          <ClockIcon />
          <span>{formatDate(row[dataKey])}</span>
        </DateCell>
      )
    }

    if (dataKey === 'type') {
      return <TypeBadge>{getVacationType(row[dataKey], t)}</TypeBadge>
    }

    if (dataKey === 'status') {
      return (
        <StatusBadge className={row[dataKey]}>
          {t('historyVacation:HistoryUser.' + row[dataKey])}
        </StatusBadge>
      )
    }

    if (dataKey === 'hours') {
      return (
        <span>
          <strong>{row[dataKey]}</strong> h
        </span>
      )
    }

    return <span>{row[dataKey]}</span>
  }

  const renderEmptyState = () => {
    if (isLoading) return null

    if (!requests || requests.length === 0) {
      return (
        <EmptyState>
          <h3>{t('historyVacation:HistoryUser.noRequests')}</h3>
          <p>{t('historyVacation:HistoryUser.createFirstRequest')}</p>
          <RequestButton onClick={handleOpenForm}>
            <PlusIcon />
            {t('historyVacation:HistoryUser.newApplication')}
          </RequestButton>
        </EmptyState>
      )
    }

    return null
  }

  return (
    <Wrap>
      <PageHeader>
        <Title>{t('historyVacation:HistoryUser.vacationHistory')}</Title>
      </PageHeader>

      <Coc>
        <RequestButton onClick={handleOpenForm}>
          <PlusIcon />
          {t('historyVacation:HistoryUser.newApplication')}
        </RequestButton>

        <DaysCount>
          <p>{t('historyVacation:HistoryUser.vacationDays')}:</p>
          <span>{profile && profile.available_vacation_days}</span>
        </DaysCount>
      </Coc>

      <Container>
        {renderEmptyState()}

        {requests && requests.length > 0 && (
          <Tables
            data={requests}
            columns={columns}
            cellRenderer={cellRenderer}
          />
        )}
      </Container>

      <VacationFormModal
        isFormVisible={isFormVisible}
        closeForm={handleCloseForm}
        refetch={refetch}
      />
    </Wrap>
  )
}

export default HistoryVacation

const getVacationType = (key: string, t: (key: string) => string): string => {
  switch (key) {
    case 'ondemand':
      return t('CompanyHistory:History.ondemand')
    case 'recreational':
      return t('CompanyHistory:History.recreational')
    case 'occasional':
      return t('CompanyHistory:History.occasional')
    case 'parental':
      return t('CompanyHistory:History.parental')
    case 'unpaid':
      return t('CompanyHistory:History.unpaid')
    case 'l4':
      return t('CompanyHistory:History.l4')
    default:
      return t('CompanyHistory:History.default')
  }
}
